import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Rocket, Mail, Lock, User } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LanguageContext";
import LanguageToggle from "../components/LanguageToggle";

export default function Login() {
  const { setUser, user } = useAuth();
  const t = useT();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (user) navigate("/dashboard", { replace: true }); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const url = mode === "login" ? "/auth/login" : "/auth/register";
      const { data } = await api.post(url, form);
      setUser(data.user);
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const demo = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email: "demo@polimi.it", password: "demo1234" });
      setUser(data.user);
      navigate("/dashboard");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-bg min-h-screen flex flex-col items-center justify-center px-5 py-10">
      <LanguageToggle className="fixed top-4 left-4 z-50" />
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="text-center mb-8">
        <div className="flex justify-center mb-4">
          <motion.div animate={{ y: [0, -10, 0] }} transition={{ repeat: Infinity, duration: 3 }}
            className="w-20 h-20 rounded-full glass glow-cyan flex items-center justify-center">
            <Rocket size={40} style={{ color: "var(--cyan)" }} />
          </motion.div>
        </div>
        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight">
          <span style={{ color: "var(--cyan)" }}>Aero</span>Learn
        </h1>
        <p className="text-white/60 mt-2 max-w-sm mx-auto">
          {t("Il gioco per superare gli esami di Ingegneria Aerospaziale al Politecnico di Milano")}
        </p>
      </motion.div>

      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.15 }}
        className="glass rounded-3xl p-6 w-full max-w-md" data-testid="auth-card">
        <div className="flex gap-2 mb-6 p-1 rounded-full bg-black/30">
          {["login", "register"].map((m) => (
            <button key={m} onClick={() => setMode(m)} data-testid={`tab-${m}`}
              className={`flex-1 py-2 rounded-full font-heading font-bold text-sm bouncy ${mode === m ? "btn-neon" : "text-white/60"}`}>
              {m === "login" ? t("Accedi") : t("Registrati")}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-3">
          {mode === "register" && (
            <div className="flex items-center gap-2 glass rounded-full px-4 py-3">
              <User size={18} className="text-white/50" />
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={t("Nome")} className="bg-transparent outline-none w-full text-white placeholder-white/40" data-testid="input-name" />
            </div>
          )}
          <div className="flex items-center gap-2 glass rounded-full px-4 py-3">
            <Mail size={18} className="text-white/50" />
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="Email" className="bg-transparent outline-none w-full text-white placeholder-white/40" data-testid="input-email" />
          </div>
          <div className="flex items-center gap-2 glass rounded-full px-4 py-3">
            <Lock size={18} className="text-white/50" />
            <input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="Password" className="bg-transparent outline-none w-full text-white placeholder-white/40" data-testid="input-password" />
          </div>
          <button type="submit" disabled={busy} className="w-full py-3 rounded-full font-heading font-bold btn-neon bouncy disabled:opacity-60" data-testid="submit-auth">
            {busy ? t("Attendere...") : mode === "login" ? t("Inizia a giocare") : t("Crea account")}
          </button>
        </form>

        <div className="flex items-center gap-3 my-4">
          <div className="flex-1 h-px bg-white/10" /><span className="text-white/40 text-xs">{t("oppure")}</span><div className="flex-1 h-px bg-white/10" />
        </div>

        <button onClick={googleLogin} className="w-full py-3 rounded-full font-heading font-bold bg-white text-gray-800 bouncy flex items-center justify-center gap-2" data-testid="google-login-btn">
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="G" className="w-5 h-5" />
          {t("Continua con Google")}
        </button>
        <button onClick={demo} className="w-full mt-3 py-2 rounded-full text-sm text-white/60 hover:text-white bouncy" data-testid="demo-btn">
          {t("Prova con account demo")}
        </button>
      </motion.div>
    </div>
  );
}
